/**
 * GrapesJS Builder - UX Interaction Enhancements
 * Adds interactive improvements to the email template builder
 */

/**
 * Initialize UX enhancements for GrapesJS editor
 * @param {Object} editor - GrapesJS editor instance
 */
export default function initGrapesJSUXEnhancements(editor) {
	if (!editor) return;

	// Add undo/redo buttons to toolbar
	addUndoRedoButtons(editor);

	// Add help button to toolbar
	addHelpButton(editor);

	// Add resizable divider between canvas and sidebar
	addResizableDivider(editor);

	// Add keyboard shortcuts
	addKeyboardShortcuts(editor);

	// Add panel collapse functionality
	addPanelCollapseFeature(editor);

	// Add component quick actions
	addQuickActions(editor);

	// Add canvas zoom controls
	addZoomControls(editor);

	// Add better asset search
	addAssetSearch(editor);

	// Add upload-date filter tabs to the asset picker
	addAssetDateTabs(editor);

	// Highlight the in-use image + confirm when an image is applied
	addCurrentImageIndicator(editor);

	// Add auto-save indicator
	addAutoSaveIndicator(editor);

	// Add helpful tooltips
	addEnhancedTooltips(editor);

	// Add undo/redo visual feedback
	addUndoRedoFeedback(editor);

	// Remove useless toolbar buttons
	removeUselessButtons(editor);
}

/**
 * Add undo/redo buttons to the toolbar
 */
function addUndoRedoButtons(editor) {
	// Add buttons immediately - the editor is already initialized when this is called
	editor.Panels.addButton('options', {
		id: 'undo',
		className: 'fa fa-undo',
		command: 'core:undo',
		attributes: { title: 'Undo (Ctrl+Z)' }
	});

	editor.Panels.addButton('options', {
		id: 'redo',
		className: 'fa fa-repeat',
		command: 'core:redo',
		attributes: { title: 'Redo (Ctrl+Y)' }
	});
}

/**
 * Add help button to the toolbar
 */
function addHelpButton(editor) {
	// Add help command
	editor.Commands.add('open-help', {
		run() {
			// Get the base URL from the current window location
			const baseUrl = window.location.origin + window.location.pathname.split('/s/')[0];
			const helpUrl = `${baseUrl}/plugins/GrapesJsBuilderBundle/Documentation/user-guide.html`;

			// Open documentation in new tab
			window.open(helpUrl, '_blank', 'noopener,noreferrer');
		}
	});

	// Add help button to options panel (top-right toolbar)
	editor.Panels.addButton('options', {
		id: 'help',
		className: 'fa fa-question-circle',
		command: 'open-help',
		attributes: {
			title: 'Help - User Guide',
			'aria-label': 'Open User Guide'
		}
	});
}

/**
 * Add resizable divider between canvas and right sidebar
 */
function addResizableDivider(editor) {
	// Try adding divider immediately, with a small delay to ensure DOM is ready
	setTimeout(() => {
		const viewsContainer = document.querySelector('.gjs-pn-views-container');

		if (!viewsContainer) {
			// Views container not found for resize divider
			return;
		}

		// Create resize divider
		const divider = document.createElement('div');
		divider.className = 'gjs-resize-divider';

		// Get saved width from localStorage or use default
		const savedWidth = localStorage.getItem('gjs-sidebar-width');
		if (savedWidth) {
			viewsContainer.style.width = savedWidth + 'px';
		}

		// Insert divider before the views container
		viewsContainer.parentNode.insertBefore(divider, viewsContainer);

		let isDragging = false;
		let startX = 0;
		let startWidth = 0;

		const onMouseDown = (e) => {
			isDragging = true;
			startX = e.clientX;
			startWidth = viewsContainer.offsetWidth;

			divider.classList.add('dragging');
			document.body.classList.add('gjs-resizing');

			e.preventDefault();
		};

		const onMouseMove = (e) => {
			if (!isDragging) return;

			const delta = startX - e.clientX; // Reversed because we're measuring from the right
			const newWidth = startWidth + delta;

			// Set minimum and maximum widths
			const minWidth = 250; // Minimum sidebar width
			const maxWidth = window.innerWidth - 400; // Leave at least 400px for canvas

			if (newWidth >= minWidth && newWidth <= maxWidth) {
				viewsContainer.style.width = newWidth + 'px';

				// Position the divider
				divider.style.right = newWidth + 'px';
			}
		};

		const onMouseUp = () => {
			if (!isDragging) return;

			isDragging = false;
			divider.classList.remove('dragging');
			document.body.classList.remove('gjs-resizing');

			// Save the width to localStorage
			localStorage.setItem('gjs-sidebar-width', viewsContainer.offsetWidth);
		};

		// Attach event listeners
		divider.addEventListener('mousedown', onMouseDown);
		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);

		// Position divider initially
		divider.style.right = viewsContainer.offsetWidth + 'px';

		// Handle window resize
		window.addEventListener('resize', () => {
			if (!isDragging) {
				divider.style.right = viewsContainer.offsetWidth + 'px';
			}
		});
	}, 100); // Small delay to ensure views container is rendered
}

/**
 * Add keyboard shortcuts for common actions
 */
function addKeyboardShortcuts(editor) {
	const commands = editor.Commands;

	// Cmd/Ctrl + S: Save
	document.addEventListener('keydown', (e) => {
		if ((e.metaKey || e.ctrlKey) && e.key === 's') {
			e.preventDefault();
			// Trigger save (assuming there's a save button)
			const saveBtn = document.querySelector('.gjs-btn-prim[type="submit"]') ||
							document.querySelector('.btn-apply');
			if (saveBtn) saveBtn.click();

			showToast('Saving...', 'info');
		}

		// Cmd/Ctrl + Z: Undo
		if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
			e.preventDefault();
			commands.run('core:undo');
			showToast('Undo', 'info');
		}

		// Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y: Redo
		if ((e.metaKey || e.ctrlKey) && (e.shiftKey && e.key === 'z') || e.key === 'y') {
			e.preventDefault();
			commands.run('core:redo');
			showToast('Redo', 'info');
		}

		// Delete: Remove selected component
		if (e.key === 'Delete' || e.key === 'Backspace') {
			const selected = editor.getSelected();
			if (selected && !isEditingText()) {
				e.preventDefault();
				commands.run('core:component-delete');
				showToast('Component deleted', 'success');
			}
		}

		// Cmd/Ctrl + D: Duplicate
		if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
			const selected = editor.getSelected();
			if (selected) {
				e.preventDefault();
				commands.run('tlb-clone');
				showToast('Component duplicated', 'success');
			}
		}

		// Escape: Deselect
		if (e.key === 'Escape') {
			editor.select();
			showToast('Selection cleared', 'info');
		}
	});

	// Add keyboard shortcuts hint panel
	addKeyboardShortcutsPanel();
}

/**
 * Check if user is editing text (including inside the GrapesJS canvas iframe)
 */
function isEditingText() {
	// Check parent document
	const activeEl = document.activeElement;
	if (activeEl && (
		activeEl.isContentEditable ||
		activeEl.tagName === 'INPUT' ||
		activeEl.tagName === 'TEXTAREA'
	)) {
		return true;
	}

	// Check GrapesJS editor editing state (covers inline editing in canvas iframe)
	if (window.Mautic && window.Mautic.builderInstance) {
		try {
			const editor = window.Mautic.builderInstance.getEditor();
			if (editor && editor.getEditing()) {
				return true;
			}
		} catch (e) {}
	}

	// Check inside the GrapesJS canvas iframe
	try {
		const iframe = document.querySelector('iframe.gjs-frame');
		if (iframe && iframe.contentDocument) {
			const iframeActiveEl = iframe.contentDocument.activeElement;
			if (iframeActiveEl && (
				iframeActiveEl.isContentEditable ||
				iframeActiveEl.tagName === 'INPUT' ||
				iframeActiveEl.tagName === 'TEXTAREA'
			)) {
				return true;
			}
		}
	} catch (e) {}

	return false;
}

/**
 * Add panel collapse/expand functionality
 */
function addPanelCollapseFeature(editor) {
	// Add collapse buttons to panel headers
	setTimeout(() => {
		const panelHeaders = document.querySelectorAll('.gjs-sm-sector .gjs-sm-title');

		panelHeaders.forEach(header => {
			if (header.querySelector('.panel-collapse-btn')) return; // Already added

			const collapseBtn = document.createElement('span');
			collapseBtn.className = 'panel-collapse-btn';
			collapseBtn.innerHTML = '<i class="fa fa-chevron-down"></i>';
			collapseBtn.style.cssText = 'float: right; cursor: pointer; transition: transform 0.2s;';

			header.appendChild(collapseBtn);

			header.addEventListener('click', function() {
				const sector = this.closest('.gjs-sm-sector');
				const properties = sector.querySelector('.gjs-sm-properties');

				if (properties) {
					const isCollapsed = properties.style.display === 'none';
					properties.style.display = isCollapsed ? 'block' : 'none';
					collapseBtn.style.transform = isCollapsed ? 'rotate(0deg)' : 'rotate(-90deg)';

					// Save state to localStorage
					const sectorTitle = this.textContent.trim();
					localStorage.setItem(`gjs-panel-${sectorTitle}`, isCollapsed ? 'expanded' : 'collapsed');
				}
			});

			// Restore collapsed state
			const sectorTitle = header.textContent.trim();
			const savedState = localStorage.getItem(`gjs-panel-${sectorTitle}`);
			if (savedState === 'collapsed') {
				const sector = header.closest('.gjs-sm-sector');
				const properties = sector?.querySelector('.gjs-sm-properties');
				if (properties) {
					properties.style.display = 'none';
					collapseBtn.style.transform = 'rotate(-90deg)';
				}
			}
		});
	}, 1000);
}

/**
 * Add quick actions for selected components
 */
function addQuickActions(editor) {
	editor.on('component:selected', (component) => {
		// Validate component exists and has required methods
		if (!component || typeof component.get !== 'function') {
			return;
		}

		try {
			// Add custom toolbar items
			const toolbar = component.get('toolbar');

			// Validate toolbar exists and is an array
			if (!toolbar || !Array.isArray(toolbar)) {
				return;
			}

			const hasQuickActions = toolbar.some(item => item.id === 'quick-style');
			const hasCodeEditor = toolbar.some(item => item.id === 'open-code-editor');

			let modified = false;

			if (!hasQuickActions) {
				toolbar.push({
					id: 'quick-style',
					label: '<i class="fa fa-paint-brush"></i>',
					command: () => {
						// Open style manager and focus on relevant section
						const styleManagerBtn = document.querySelector('[data-toggle-panel="Style Manager"]');
						if (styleManagerBtn) styleManagerBtn.click();
						showToast('Style panel opened', 'info');
					},
					attributes: { title: 'Quick Style' }
				});
				modified = true;
			}

			if (!hasCodeEditor) {
				toolbar.push({
					id: 'open-code-editor',
					label: '<i class="fa fa-code"></i>',
					command: (ed) => {
						// Open code editor and focus on this component
						openCodeEditorForComponent(ed || editor, component);
					},
					attributes: { title: 'Edit in Code Editor' }
				});
				modified = true;
			}

			if (modified) {
				component.set('toolbar', toolbar);
			}
		} catch (error) {
			// Silently handle errors to avoid breaking panel buttons
		}
	});
}

/**
 * Open code editor with focus on specific component
 */
function openCodeEditorForComponent(editor, component) {
	try {
		// Store the target component in editor config for the command to access
		editor._targetComponentForCodeEditor = component;

		// Run the command
		editor.runCommand('preset-mautic:code-edit');

		showToast('Opening code editor...', 'info');
	} catch (error) {
		console.error('Failed to open code editor:', error);
		showToast('Failed to open code editor', 'error');
	}
}

/**
 * Add canvas zoom controls
 */
function addZoomControls(editor) {
	const canvas = editor.Canvas;
	let zoomLevel = 1;

	// Create zoom controls UI
	const zoomControls = document.createElement('div');
	zoomControls.className = 'gjs-zoom-controls';
	zoomControls.style.cssText = `
		position: fixed;
		bottom: 20px;
		right: 20px;
		background: var(--gjs-color-bg-main, #2a2a2a);
		border: 1px solid var(--gjs-color-border, #404040);
		border-radius: 8px;
		padding: 8px;
		display: flex;
		gap: 8px;
		z-index: 1000;
		box-shadow: 0 4px 12px rgba(0,0,0,0.3);
	`;

	zoomControls.innerHTML = `
		<button id="gjs-zoom-out" class="gjs-zoom-btn" title="Zoom Out">
			<i class="fa fa-minus"></i>
		</button>
		<span id="gjs-zoom-level" style="color: #fff; min-width: 50px; text-align: center;">100%</span>
		<button id="gjs-zoom-in" class="gjs-zoom-btn" title="Zoom In">
			<i class="fa fa-plus"></i>
		</button>
		<button id="gjs-zoom-reset" class="gjs-zoom-btn" title="Reset Zoom">
			<i class="fa fa-compress"></i>
		</button>
	`;

	// Add button styles
	const style = document.createElement('style');
	style.textContent = `
		.gjs-zoom-btn {
			background: transparent;
			border: 1px solid var(--gjs-color-border, #404040);
			color: var(--gjs-color-text-secondary, #b0b0b0);
			padding: 6px 10px;
			border-radius: 4px;
			cursor: pointer;
			transition: all 0.2s;
		}
		.gjs-zoom-btn:hover {
			background: var(--gjs-color-bg-hover, #4a4a4a);
			color: var(--gjs-color-text-primary, #fff);
		}
		.gjs-zoom-btn:active {
			transform: scale(0.95);
		}
		.gjs-cv-canvas {
			transition: transform 0.2s ease-in-out;
		}
	`;
	document.head.appendChild(style);

	// Wait for canvas to be ready
	editor.on('load', () => {
		setTimeout(() => {
			const canvasElement = document.querySelector('.gjs-cv-canvas');
			if (canvasElement && !document.querySelector('.gjs-zoom-controls')) {
				document.body.appendChild(zoomControls);

				// Zoom functions
				function updateZoom(delta) {
					zoomLevel = Math.max(0.25, Math.min(2, zoomLevel + delta));

					// Try multiple approaches to zoom
					const frame = canvas.getFrame();
					const frameEl = frame?.el || frame;
					const canvasBody = canvas.getBody();

					// Apply zoom to canvas wrapper
					const canvasWrapper = document.querySelector('.gjs-cv-canvas__frames');
					const targetElement = canvasWrapper || canvasElement;

					if (targetElement) {
						targetElement.style.transform = `scale(${zoomLevel})`;
						targetElement.style.transformOrigin = 'top center';
					}

					// Update zoom level display
					const levelSpan = document.getElementById('gjs-zoom-level');
					if (levelSpan) {
						levelSpan.textContent = `${Math.round(zoomLevel * 100)}%`;
					}

					// Show toast
					showToast(`Zoom: ${Math.round(zoomLevel * 100)}%`, 'info', 1000);
				}

				// Attach event listeners
				const zoomInBtn = document.getElementById('gjs-zoom-in');
				const zoomOutBtn = document.getElementById('gjs-zoom-out');
				const zoomResetBtn = document.getElementById('gjs-zoom-reset');

				if (zoomInBtn) {
					zoomInBtn.addEventListener('click', (e) => {
						e.preventDefault();
						e.stopPropagation();
						updateZoom(0.1);
					});
				}

				if (zoomOutBtn) {
					zoomOutBtn.addEventListener('click', (e) => {
						e.preventDefault();
						e.stopPropagation();
						updateZoom(-0.1);
					});
				}

				if (zoomResetBtn) {
					zoomResetBtn.addEventListener('click', (e) => {
						e.preventDefault();
						e.stopPropagation();
						zoomLevel = 1;
						updateZoom(0);
					});
				}
			}
		}, 1500);
	});
}

/**
 * Add asset search functionality
 */
function addAssetSearch(editor) {
	editor.on('run:open-assets', () => {
		setTimeout(() => {
			const assetManager = document.querySelector('.gjs-am-assets-cont');
			if (!assetManager || assetManager.querySelector('.gjs-asset-search')) return;

			const searchBox = document.createElement('input');
			searchBox.type = 'text';
			searchBox.className = 'gjs-asset-search gjs-field';
			searchBox.placeholder = 'Search assets...';
			searchBox.style.cssText = 'margin: 12px; width: calc(100% - 24px);';

			assetManager.insertBefore(searchBox, assetManager.firstChild);

			searchBox.addEventListener('input', (e) => {
				const searchTerm = e.target.value.toLowerCase();
				const assets = document.querySelectorAll('.gjs-am-asset');

				assets.forEach(asset => {
					const name = asset.querySelector('.gjs-am-name')?.textContent.toLowerCase() || '';
					asset.style.display = name.includes(searchTerm) ? 'block' : 'none';
				});
			});
		}, 100);
	});
}

/**
 * Add upload-date filter tabs to the "Select Image" asset picker. Each tab re-renders the
 * grid with a date subset via GrapesJS's native AssetManager.render(filteredArray). The date
 * is carried on each asset from the server feed (asset.get('date'), a unix timestamp).
 */
function addAssetDateTabs(editor) {
	// asset:open is the event that reliably fires when the image picker opens (the same one
	// addCurrentImageIndicator uses); run:open-assets is kept as a belt-and-braces fallback.
	const onOpen = () => {
		setTimeout(() => {
			const cont = document.querySelector('.gjs-am-assets-cont');
			if (!cont || cont.querySelector('.gjs-am-date-tabs')) return;

			const am     = editor.AssetManager;
			const nowSec = Date.now() / 1000;
			const DAY    = 86400;
			const bucketOf = (ts) => {
				if (!ts) return 'older';
				if (ts >= nowSec - 31 * DAY) return 'month';
				if (ts >= nowSec - 92 * DAY) return 'quarter';
				return 'older';
			};

			const tabs = [
				{ key: 'all', label: 'All' },
				{ key: 'month', label: 'This month' },
				{ key: 'quarter', label: 'Last 3 months' },
				{ key: 'older', label: 'Older' },
			];

			const bar = document.createElement('div');
			bar.className = 'gjs-am-date-tabs';
			bar.style.cssText = 'display:flex; gap:6px; margin:0 12px 8px; flex-wrap:wrap;';

			tabs.forEach((t) => {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.textContent = t.label;
				btn.style.cssText = 'padding:4px 10px; font-size:12px; border:1px solid rgba(255,255,255,0.25); border-radius:3px; background:transparent; color:inherit; cursor:pointer; opacity:' + ('all' === t.key ? '1' : '0.55') + ';';
				btn.addEventListener('click', () => {
					bar.querySelectorAll('button').forEach((b) => { b.style.opacity = '0.55'; });
					btn.style.opacity = '1';
					const all = am.getAll();
					am.render('all' === t.key ? all.filter(() => true) : all.filter((a) => bucketOf(a.get('date')) === t.key));
				});
				bar.appendChild(btn);
			});

			// Place the tabs right after the search box (added by addAssetSearch).
			const search = cont.querySelector('.gjs-asset-search');
			if (search && search.nextSibling) {
				cont.insertBefore(bar, search.nextSibling);
			} else {
				cont.insertBefore(bar, cont.firstChild);
			}
		}, 120);
	};

	editor.on('asset:open', onOpen);
	editor.on('run:open-assets', onOpen);
}

/**
 * Asset Manager: highlight the image that's currently applied to the component
 * being edited, and confirm with a toast once an image is actually applied.
 *
 * - On `asset:open` we read the edited image's current src, then outline the
 *   matching thumbnail in the grid and tag it with an "In use" badge.
 * - On `asset:close` we compare the src again; if it changed (or a brand-new
 *   image got a src), we show an "Image updated" confirmation.
 */
function addCurrentImageIndicator(editor) {
	const HIGHLIGHT = 'gjs-am-asset-current';
	let openedSrc = null; // src of the edited image when the manager was opened
	let lastToastAt = 0;  // dedupe: asset:close can fire more than once per close

	// Read the src of an image-ish component (image / mj-image expose it as a
	// model prop and/or an HTML attribute).
	const readSrc = (cmp) => {
		if (!cmp || typeof cmp.get !== 'function') return null;
		const attrs = typeof cmp.getAttributes === 'function' ? cmp.getAttributes() : {};
		return cmp.get('src') || attrs.src || null;
	};

	// Normalize URLs so CDN/origin/protocol/query differences don't break the
	// match (compare on path only, case-insensitively).
	const normalize = (url) => {
		if (!url) return '';
		try {
			const a = document.createElement('a');
			a.href = url;
			return (a.pathname || String(url)).replace(/\/+$/, '').toLowerCase();
		} catch (e) {
			return String(url).toLowerCase();
		}
	};

	// Extract the image src from a rendered `.gjs-am-asset` element, covering
	// both the <img> and background-image rendering variants.
	const assetElSrc = (el) => {
		const img = el.querySelector('img[src]');
		if (img) return img.getAttribute('src');
		const bgEl = el.querySelector('[style*="background-image"]') || el;
		const style = bgEl && bgEl.getAttribute ? bgEl.getAttribute('style') : '';
		const m = style && style.match(/background-image\s*:\s*url\(["']?([^"')]+)["']?\)/i);
		return m ? m[1] : null;
	};

	editor.on('asset:open', () => {
		openedSrc = readSrc(editor.getSelected());
		const target = normalize(openedSrc);

		// The grid renders asynchronously, mirror addAssetSearch's deferral.
		setTimeout(() => {
			const assets = document.querySelectorAll('.gjs-am-asset');
			let matched = null;

			assets.forEach((el) => {
				// Clear any stale highlight first (the manager is reused).
				el.classList.remove(HIGHLIGHT);
				el.style.removeProperty('outline');
				el.style.removeProperty('outline-offset');
				const oldBadge = el.querySelector('.gjs-am-current-badge');
				if (oldBadge) oldBadge.remove();

				if (target && normalize(assetElSrc(el)) === target) {
					matched = el;
					el.classList.add(HIGHLIGHT);
					el.style.outline = '3px solid var(--gjs-color-primary, #5e82f4)';
					el.style.outlineOffset = '-1px';
					if (!el.style.position) el.style.position = 'relative';

					const badge = document.createElement('span');
					badge.className = 'gjs-am-current-badge';
					badge.textContent = 'In use';
					badge.style.cssText = `
						position: absolute; top: 4px; left: 4px; z-index: 2;
						background: var(--gjs-color-primary, #5e82f4); color: #fff;
						font-size: 10px; font-weight: 600; line-height: 1;
						padding: 3px 6px; border-radius: 3px;
						box-shadow: 0 1px 3px rgba(0,0,0,0.4); pointer-events: none;
					`;
					el.appendChild(badge);
				}
			});

			if (matched && typeof matched.scrollIntoView === 'function') {
				matched.scrollIntoView({ block: 'nearest' });
			}
		}, 150);
	});

	editor.on('asset:close', () => {
		const newSrc = normalize(readSrc(editor.getSelected()));
		const now = Date.now();
		if (newSrc && newSrc !== normalize(openedSrc) && now - lastToastAt > 800) {
			lastToastAt = now;
			showToast('Image updated', 'success');
		}
		openedSrc = null;
	});
}

/**
 * Add auto-save indicator
 */
function addAutoSaveIndicator(editor) {
	const indicator = document.createElement('div');
	indicator.className = 'gjs-autosave-indicator';
	indicator.style.cssText = `
		position: fixed;
		top: 50px;
		right: 20px;
		background: var(--gjs-color-success, #10b981);
		color: white;
		padding: 8px 16px;
		border-radius: 6px;
		font-size: 13px;
		font-weight: 500;
		opacity: 0;
		transform: translateY(-10px);
		transition: all 0.3s;
		z-index: 10000;
		pointer-events: none;
		box-shadow: 0 4px 12px rgba(0,0,0,0.3);
	`;
	indicator.innerHTML = '<i class="fa fa-check-circle"></i> Saved';
	document.body.appendChild(indicator);

	// Show indicator when storage is updated
	editor.on('storage:end', () => {
		indicator.style.opacity = '1';
		indicator.style.transform = 'translateY(0)';

		setTimeout(() => {
			indicator.style.opacity = '0';
			indicator.style.transform = 'translateY(-10px)';
		}, 2000);
	});
}

/**
 * Add enhanced tooltips
 */
function addEnhancedTooltips(editor) {
	// Add tooltips to panel buttons - run multiple times to catch dynamically added buttons
	const addTooltipsOnce = () => {
		const buttons = document.querySelectorAll('.gjs-pn-btn');

		buttons.forEach(btn => {
			const currentTitle = btn.getAttribute('title') || '';
			const btnClasses = btn.className || '';
			const icon = btn.querySelector('svg, i, span[class*="fa-"]');
			const iconClasses = icon ? (icon.className.baseVal || icon.className || '') : '';

			// Fix untranslated keys or add missing tooltips
			let newTitle = '';

			// Fix translation keys
			if (currentTitle.includes('grapesjsbuilder.panelsViewsButtonsApplyTitle')) {
				newTitle = 'Apply Changes';
			} else if (currentTitle.includes('grapesjsbuilder.buttons.buttonPreview.title')) {
				newTitle = 'Preview Email';
			}
			// Add tooltips for buttons without titles or improve existing ones
			else if (!currentTitle || currentTitle === '(none)') {
				// Identify button by icon classes
				if (iconClasses.includes('fa-undo') || btnClasses.includes('fa-undo')) {
					newTitle = 'Undo (Ctrl+Z)';
				} else if (iconClasses.includes('fa-repeat') || iconClasses.includes('fa-redo') || btnClasses.includes('fa-repeat')) {
					newTitle = 'Redo (Ctrl+Y)';
				} else if (iconClasses.includes('fa-moon') || btnClasses.includes('fa-moon')) {
					newTitle = 'Switch to Dark Mode';
				} else if (iconClasses.includes('fa-sun') || btnClasses.includes('fa-sun')) {
					newTitle = 'Switch to Light Mode';
				} else if (iconClasses.includes('fa-square-o') || btnClasses.includes('fa-square-o')) {
					newTitle = 'View Components';
				} else if (iconClasses.includes('fa-arrows-alt') || btnClasses.includes('fa-arrows-alt')) {
					newTitle = 'Toggle Fullscreen';
				} else if (iconClasses.includes('fa-code') || btnClasses.includes('fa-code')) {
					newTitle = 'View Code';
				} else if (iconClasses.includes('fa-edit') || btnClasses.includes('fa-edit')) {
					newTitle = 'Edit Code';
				} else if (iconClasses.includes('fa-th') || btnClasses.includes('fa-th')) {
					newTitle = 'Toggle Grid (Shift+G)';
				} else if (iconClasses.includes('fa-magnet') || btnClasses.includes('fa-magnet')) {
					newTitle = 'Toggle Snap to Grid (Shift+S)';
				} else if (iconClasses.includes('fa-keyboard-o') || btnClasses.includes('fa-keyboard-o')) {
					newTitle = 'Keyboard Shortcuts (?)';
				} else if (iconClasses.includes('fa-paint-brush') || btnClasses.includes('fa-paint-brush')) {
					newTitle = 'Open Style Manager';
				} else if (iconClasses.includes('fa-bars') || btnClasses.includes('fa-bars')) {
					newTitle = 'Open Layer Manager';
				} else if (iconClasses.includes('fa-th-large') || btnClasses.includes('fa-th-large')) {
					newTitle = 'Open Blocks';
				} else if (iconClasses.includes('fa-check') || btnClasses.includes('fa-check')) {
					newTitle = 'Apply Changes';
				} else if (iconClasses.includes('fa-times-circle') || btnClasses.includes('fa-times-circle')) {
					newTitle = 'Close';
				} else if (iconClasses.includes('fa-external-link') || btnClasses.includes('fa-external-link')) {
					newTitle = 'Preview Email';
				}
				// Check for SVG-based device icons
				else if (icon && icon.tagName === 'svg') {
					const svgHTML = icon.outerHTML || '';
					if (svgHTML.includes('M20 2H4c-1')) {
						newTitle = 'Desktop View';
					} else if (svgHTML.includes('M19 2H5c-1')) {
						newTitle = 'Tablet View';
					} else if (svgHTML.includes('M7 2h10')) {
						newTitle = 'Mobile View';
					}
				}
			}

			// Apply the new tooltip if we found one
			if (newTitle) {
				btn.setAttribute('title', newTitle);
				btn.setAttribute('aria-label', newTitle);
			}
		});
	};

	// Run immediately and then after delays to catch dynamically loaded buttons
	addTooltipsOnce();
	setTimeout(addTooltipsOnce, 1000);
	setTimeout(addTooltipsOnce, 2000);
	setTimeout(addTooltipsOnce, 3000);

	// Also run when panels are opened
	editor.on('run:open-sm', addTooltipsOnce);
	editor.on('run:open-layers', addTooltipsOnce);
	editor.on('run:open-blocks', addTooltipsOnce);

	// Final pass: add ARIA labels to all buttons that have tooltips but no ARIA label
	setTimeout(() => {
		const buttons = document.querySelectorAll('.gjs-pn-btn[title]:not([aria-label])');
		buttons.forEach(btn => {
			const title = btn.getAttribute('title');
			if (title && title !== '(none)') {
				btn.setAttribute('aria-label', title);
			}
		});
	}, 4000);
}

/**
 * Add undo/redo visual feedback
 */
function addUndoRedoFeedback(editor) {
	const undoManager = editor.UndoManager;

	editor.on('undo', () => showToast('Undo', 'info', 1000));
	editor.on('redo', () => showToast('Redo', 'info', 1000));
}

/**
 * Add keyboard shortcuts panel
 */
function addKeyboardShortcutsPanel() {
	// Don't add if already exists
	if (document.querySelector('.gjs-keyboard-shortcuts-btn')) return;

	// Create shortcut hint button
	const shortcutBtn = document.createElement('button');
	shortcutBtn.className = 'gjs-keyboard-shortcuts-btn';
	shortcutBtn.innerHTML = '<i class="fa fa-keyboard-o"></i>';
	shortcutBtn.title = 'Keyboard Shortcuts (?)';
	shortcutBtn.style.cssText = `
		position: fixed;
		bottom: 20px;
		left: 20px;
		background: var(--gjs-color-bg-main, #2a2a2a);
		border: 1px solid var(--gjs-color-border, #404040);
		color: var(--gjs-color-text-secondary, #b0b0b0);
		padding: 10px 12px;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s;
		z-index: 1000;
		box-shadow: 0 4px 12px rgba(0,0,0,0.3);
	`;

	shortcutBtn.addEventListener('click', (e) => {
		e.preventDefault();
		e.stopPropagation();
		showKeyboardShortcuts();
	});

	shortcutBtn.addEventListener('mouseenter', function() {
		this.style.background = 'var(--gjs-color-bg-hover, #4a4a4a)';
		this.style.color = 'var(--gjs-color-text-primary, #fff)';
	});

	shortcutBtn.addEventListener('mouseleave', function() {
		this.style.background = 'var(--gjs-color-bg-main, #2a2a2a)';
		this.style.color = 'var(--gjs-color-text-secondary, #b0b0b0)';
	});

	setTimeout(() => {
		document.body.appendChild(shortcutBtn);
	}, 1000);
}

/**
 * Show keyboard shortcuts modal
 */
function showKeyboardShortcuts() {
	const modal = document.createElement('div');
	modal.className = 'gjs-shortcuts-modal';
	modal.style.cssText = `
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0,0,0,0.7);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 100000;
	`;

	modal.innerHTML = `
		<div style="background: var(--gjs-color-bg-panel, #2a2a2a); border-radius: 12px; padding: 32px; max-width: 600px; box-shadow: 0 20px 60px rgba(0,0,0,0.5);">
			<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
				<h2 style="color: var(--gjs-color-text-primary, #fff); margin: 0; font-size: 24px;">Keyboard Shortcuts</h2>
				<button class="close-shortcuts" style="background: transparent; border: none; color: var(--gjs-color-text-secondary, #b0b0b0); font-size: 24px; cursor: pointer;">&times;</button>
			</div>
			<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; color: var(--gjs-color-text-secondary, #b0b0b0); font-size: 14px;">
				<div><kbd>Ctrl/Cmd + S</kbd> <span>Save</span></div>
				<div><kbd>Ctrl/Cmd + Z</kbd> <span>Undo</span></div>
				<div><kbd>Ctrl/Cmd + Y</kbd> <span>Redo</span></div>
				<div><kbd>Ctrl/Cmd + D</kbd> <span>Duplicate</span></div>
				<div><kbd>Delete</kbd> <span>Remove component</span></div>
				<div><kbd>Esc</kbd> <span>Deselect</span></div>
			</div>
		</div>
	`;

	// Add kbd styling
	const style = document.createElement('style');
	style.textContent = `
		.gjs-shortcuts-modal kbd {
			display: inline-block;
			padding: 4px 8px;
			background: var(--gjs-color-bg-tertiary, #424242);
			border: 1px solid var(--gjs-color-border, #404040);
			border-radius: 4px;
			font-family: monospace;
			font-size: 12px;
			margin-right: 8px;
			min-width: 100px;
			text-align: center;
		}
	`;
	document.head.appendChild(style);

	document.body.appendChild(modal);

	modal.addEventListener('click', (e) => {
		if (e.target === modal) modal.remove();
	});

	modal.querySelector('.close-shortcuts').addEventListener('click', () => {
		modal.remove();
	});
}

/**
 * Show toast notification
 */
function showToast(message, type = 'info', duration = 2000) {
	const toast = document.createElement('div');
	toast.className = 'gjs-toast';

	const colors = {
		info: 'var(--gjs-color-primary, #5e82f4)',
		success: 'var(--gjs-color-success, #10b981)',
		error: 'var(--gjs-color-danger, #ef4444)',
		warning: 'var(--gjs-color-warning, #f59e0b)'
	};

	toast.style.cssText = `
		position: fixed;
		top: 20px;
		left: 50%;
		transform: translateX(-50%) translateY(-20px);
		background: ${colors[type]};
		color: white;
		padding: 12px 24px;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 500;
		opacity: 0;
		transition: all 0.3s;
		z-index: 100000;
		box-shadow: 0 4px 12px rgba(0,0,0,0.3);
	`;
	toast.textContent = message;
	document.body.appendChild(toast);

	setTimeout(() => {
		toast.style.opacity = '1';
		toast.style.transform = 'translateX(-50%) translateY(0)';
	}, 10);

	setTimeout(() => {
		toast.style.opacity = '0';
		toast.style.transform = 'translateX(-50%) translateY(-20px)';
		setTimeout(() => toast.remove(), 300);
	}, duration);
}

/**
 * Remove useless toolbar buttons (grid, magnet that don't work)
 * Note: Undo/redo buttons are kept as they provide useful visual feedback
 */
function removeUselessButtons(editor) {
	editor.on('load', () => {
		setTimeout(() => {
			// Keep undo/redo buttons - they work via keyboard shortcuts and provide visual feedback
			// const undoBtn = document.querySelector('button[data-command="core:undo"]');
			// const redoBtn = document.querySelector('button[data-command="core:redo"]');
			//
			// if (undoBtn) {
			// 	undoBtn.remove();
			// 	console.log('✓ Removed undo button');
			// }
			//
			// if (redoBtn) {
			// 	redoBtn.remove();
			// 	console.log('✓ Removed redo button');
			// }

			// Remove all buttons in the options panel that match grid or magnet patterns
			const optionsPanel = document.querySelector('.gjs-pn-options');
			if (optionsPanel) {
				const allButtons = optionsPanel.querySelectorAll('.gjs-pn-btn');
				let removedCount = 0;

				allButtons.forEach((btn, index) => {
					// Get all possible identifiers
					const title = btn.getAttribute('title') || '';
					const ariaLabel = btn.getAttribute('aria-label') || '';
					const className = btn.className || '';
					const dataCommand = btn.getAttribute('data-command') || '';

					// Check ALL SVG paths in the button (not just first one)
					const allPaths = btn.querySelectorAll('svg path');
					let hasGridIcon = false;
					let pathData = '';

					allPaths.forEach(path => {
						const d = path.getAttribute('d') || '';
						pathData += d + ' ';
						// Grid pattern - look for multiple small squares
						if (d.includes('M3 3h7v7H3z') ||
						    d.includes('M3 13h7v7H3z') ||
						    d.includes('M13 3h7v7h-7z') ||
						    d.includes('M13 13h7v7h-7z')) {
							hasGridIcon = true;
						}
					});

					// Check for magnet/U-shaped icon patterns
					const hasMagnetIcon = pathData.includes('M7.5 2A5.5') || // Typical magnet path
					                     pathData.includes('M6 3v14') || // U-shape
					                     pathData.toLowerCase().includes('magnet');

					// Check for keywords in any attribute
					const hasMagnetKeyword = title.toLowerCase().includes('magnet') ||
					                        title.toLowerCase().includes('snap') ||
					                        ariaLabel.toLowerCase().includes('magnet') ||
					                        ariaLabel.toLowerCase().includes('snap') ||
					                        className.toLowerCase().includes('magnet') ||
					                        className.toLowerCase().includes('snap') ||
					                        dataCommand.toLowerCase().includes('magnet') ||
					                        dataCommand.toLowerCase().includes('snap');

					// Remove if matches any pattern
					if (hasGridIcon || hasMagnetIcon || hasMagnetKeyword) {
						btn.remove();
						removedCount++;
					}
				});
			}
		}, 3000); // Increased timeout to 3 seconds to ensure DOM is fully ready
	});
}
